import { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, BtnWarning, Button } from '@components/buttons';
import { showToast } from '@components/toaster';
import { TrackerFieldKey } from 'shared/tracker-config';
import {
  betLabel,
  betOf,
  channelOf,
  FIELD_LIMITS,
  fmtTrackerDate,
  moneyInputValue,
  nextText,
  nextStepName,
  parseMoneyCents,
  personName,
  PICK_A_BET_MESSAGE,
  sortTouches,
  stageLabel,
  stageRole,
  touchText,
  TrackerPersonFields,
} from 'shared/tracker-people';
import {
  AMOUNT_BOOKED_MESSAGE,
  AMOUNT_COLLECTED_MESSAGE,
  FUTURE_DATE_MESSAGE,
} from 'shared/tracker-copy';
import { FieldError, Panel } from './panel';
import {
  HELP_CLASS,
  INPUT_CLASS,
  LABEL_CLASS,
  LINK_CLASS,
  Person,
  PersonResult,
  readError,
  stageChipClass,
  Touch,
  TrackerData,
} from './helpers';
import { BetSelect } from './bets';

type Props = {
  data: TrackerData;
  courseId: UUID;
  person: Person;
  touches: Touch[];
  onResult: (result: PersonResult) => void;
  onDeleted: (personId: string) => void;
  openLog: (payment?: boolean) => void;
  close: () => void;
  /** A coach's view: no Change, Clear, Edit or Delete. */
  readOnly?: boolean;
  /** Opens the details form, for "Pick a bet" on a person with none. */
  onEdit?: () => void;
  /** Opens "Add your bets first", for "Pick a bet" while there are no bets. */
  openBets?: () => void;
};

export function PersonPanel(props: Props) {
  const { data, person, close } = props;
  const [editing, setEditing] = useState(false);
  const role = stageRole(data.config, person.stageKey);
  const isExit = role === 'replied_no' || role === 'lost';
  const isWon = role === 'won';
  const name = personName(person);
  return (
    <Panel
      title={name}
      close={close}
      header={
        !editing && (
          <div>
            <BtnSecondary onClick={() => setEditing(true)}>Edit</BtnSecondary>
          </div>
        )
      }
      footer={
        !editing &&
        !isExit && (
          <BtnPrimary onClick={() => props.openLog(isWon)}>
            {isWon ? 'Log payment' : 'Log touch'}
          </BtnPrimary>
        )
      }
    >
      {editing ? (
        <EditDetails {...props} done={() => setEditing(false)} />
      ) : (
        <>
          <Facts {...props} onEdit={() => setEditing(true)} />
          {isExit && (
            <Button class={`${LINK_CLASS} self-start`} onClick={() => props.openLog(true)}>
              Log payment
            </Button>
          )}
          <Timeline {...props} />
          <DeletePerson {...props} />
        </>
      )}
    </Panel>
  );
}

function FactRow({ label, children }: { label: string; children: ComponentChildren }) {
  return (
    <div class="grid grid-cols-[6rem_1fr] gap-3 py-1.5">
      <dt class="text-sm text-gray-500 dark:text-gray-400">{label}</dt>
      <dd class="text-sm text-gray-900 dark:text-gray-100 min-w-0 break-words">{children}</dd>
    </div>
  );
}

function OptionalContact({ value, href }: { value: string; href: string }) {
  if (!value) {
    return (
      <span class="text-gray-400">
        Not added <span class="text-xs">optional</span>
      </span>
    );
  }
  return (
    <a class="text-indigo-600 dark:text-indigo-400 underline" href={href}>
      {value}
    </a>
  );
}

export function Facts({
  data,
  courseId,
  person,
  touches,
  onResult,
  readOnly,
  onEdit,
  openBets,
}: Props) {
  const { config } = data;
  const fields = config.fields;
  const [changing, setChanging] = useState(false);
  const [step, setStep] = useState('');
  const [on, setOn] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const channel = channelOf(config, person.channelKey);
  const next = nextText(config, person, touches, data.today, data.participant.closeAs);

  function openChange() {
    setStep(nextStepName(config, person));
    setOn(person.nextOn ?? '');
    setError(undefined);
    setChanging(true);
  }

  async function saveNext(clear: boolean) {
    setSaving(true);
    setError(undefined);
    const match = config.sequence.find((s) => s.name.toLowerCase() === step.trim().toLowerCase());
    try {
      const result = await rpx.tracker.updatePerson({
        courseId,
        personId: person.id as UUID,
        next: clear
          ? { step: '' }
          : { step: step.trim(), stepKey: match?.key, on: on || undefined },
      });
      onResult(result);
      setChanging(false);
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  const bet = betOf(data.bets, person.betId);
  const pickBet = data.bets.length ? onEdit : openBets;
  return (
    <dl class="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
      {config.bets && (
        <FactRow label="Bet">
          {bet ? (
            betLabel(bet)
          ) : (
            <span class="flex flex-wrap items-center gap-x-3">
              None yet
              {!readOnly && pickBet && (
                <Button class={LINK_CLASS} onClick={pickBet}>
                  Pick a bet
                </Button>
              )}
            </span>
          )}
        </FactRow>
      )}
      {fields.offer && (!config.bets || !bet) && person.offer && (
        <FactRow label="Offer">{person.offer}</FactRow>
      )}
      {config.channels.length > 1 && <FactRow label="Channel">{channel?.label}</FactRow>}
      <FactRow label="Stage">
        <span
          class={`px-2 py-0.5 border rounded-full text-xs font-medium ${stageChipClass(
            config,
            person.stageKey,
          )}`}
        >
          {stageLabel(config, person.stageKey)}
        </span>
      </FactRow>
      <FactRow label="Next">
        {changing ? (
          <form
            class="flex flex-col gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              saveNext(false);
            }}
          >
            <input
              class={INPUT_CLASS}
              aria-label="Next step"
              list="tracker-sequence-steps"
              maxLength={60}
              value={step}
              onInput={(e) => setStep(e.currentTarget.value)}
            />
            <datalist id="tracker-sequence-steps">
              {config.sequence.map((s) => (
                <option key={s.key} value={s.name} />
              ))}
            </datalist>
            <input
              type="date"
              class={`${INPUT_CLASS} max-w-44`}
              aria-label="Next date"
              value={on}
              onChange={(e) => setOn(e.currentTarget.value)}
            />
            <FieldError id="next-error" message={error} />
            <div class="flex gap-2">
              <BtnPrimary type="submit" isLoading={saving}>
                Save
              </BtnPrimary>
              <BtnSecondary type="button" onClick={() => setChanging(false)}>
                Cancel
              </BtnSecondary>
            </div>
          </form>
        ) : (
          <span class="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span
              class={
                next.isLate
                  ? 'text-red-700 dark:text-red-400'
                  : next.isMuted
                    ? 'text-gray-500 dark:text-gray-400'
                    : ''
              }
            >
              {next.text || '–'}
            </span>
            {!readOnly && (
              <Button class={LINK_CLASS} onClick={openChange}>
                Change
              </Button>
            )}
            {!readOnly && (person.nextStep || person.nextOn) && (
              <Button class={LINK_CLASS} onClick={() => saveNext(true)}>
                Clear
              </Button>
            )}
          </span>
        )}
      </FactRow>
      {fields.email && (!readOnly || person.email) && (
        <FactRow label="Email">
          <OptionalContact value={person.email} href={`mailto:${person.email}`} />
        </FactRow>
      )}
      {fields.phone && (!readOnly || person.phone) && (
        <FactRow label="Phone">
          <OptionalContact
            value={person.phone}
            href={`tel:${person.phone.replace(/[^\d+]/g, '')}`}
          />
        </FactRow>
      )}
      {fields.segment && person.segment && <FactRow label="Segment">{person.segment}</FactRow>}
      {fields.tier && person.tier && <FactRow label="Tier">{person.tier}</FactRow>}
      {fields.note && person.note && (
        <FactRow label="Note">
          <span class="whitespace-pre-line">{person.note}</span>
        </FactRow>
      )}
    </dl>
  );
}

function restoreInput(touch: Touch) {
  return {
    personId: touch.personId as UUID,
    kind: touch.kind,
    stageKey: touch.stageKey,
    stepKey: touch.stepKey,
    label: touch.label,
    happenedOn: touch.happenedOn,
    forOn: touch.forOn,
    amountCents: touch.amountCents,
    createdAt:
      touch.createdAt instanceof Date ? touch.createdAt.toISOString() : String(touch.createdAt),
  };
}

export function Timeline({ data, courseId, person, touches, onResult, readOnly }: Props) {
  const { config, today } = data;
  const [editingId, setEditingId] = useState<string | undefined>();
  const newestFirst = sortTouches(touches).reverse();

  async function remove(touch: Touch) {
    const previousNext = { step: person.nextStep, stepKey: person.nextStepKey, on: person.nextOn };
    try {
      const results = await rpx.tracker.deleteTouches({ courseId, touchIds: [touch.id as UUID] });
      results.forEach(onResult);
      showToast({
        type: 'ok',
        title: 'Touch deleted',
        message: touchText(config, touch, today),
        timeout: 8000,
        action: {
          label: 'Undo',
          onClick: () => {
            rpx.tracker
              .restoreTouch({ courseId, touch: restoreInput(touch), restoreNext: previousNext })
              .then(onResult)
              .catch((err) =>
                showToast({
                  type: 'warn',
                  title: 'Couldn’t undo',
                  message: readError(err).message,
                }),
              );
          },
        },
      });
    } catch (err) {
      showToast({ type: 'warn', title: 'Couldn’t delete', message: readError(err).message });
    }
  }

  return (
    <section class="flex flex-col gap-2">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Timeline</h3>
      {!newestFirst.length && (
        <p class="text-sm text-gray-500 dark:text-gray-400">
          {readOnly ? 'No touches yet.' : 'No touches yet. Log the first one when you send it.'}
        </p>
      )}
      <ol class="flex flex-col divide-y divide-gray-100 dark:divide-gray-700">
        {newestFirst.map((touch) =>
          editingId === touch.id ? (
            <li key={touch.id} class="py-2">
              <EditTouch
                data={data}
                courseId={courseId}
                touch={touch}
                onResult={onResult}
                done={() => setEditingId(undefined)}
              />
            </li>
          ) : (
            <li
              key={touch.id}
              class="py-2 grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-baseline gap-x-3"
            >
              <span class="text-xs text-gray-500 dark:text-gray-400">
                {fmtTrackerDate(touch.happenedOn, today)}
              </span>
              <span class="text-sm text-gray-900 dark:text-gray-100 min-w-0 break-words">
                {touchText(config, touch, today)}
              </span>
              {!readOnly && (
                <span class="flex gap-3 justify-end">
                  <Button class={LINK_CLASS} onClick={() => setEditingId(touch.id)}>
                    Edit
                  </Button>
                  <Button class={LINK_CLASS} onClick={() => remove(touch)}>
                    Delete
                  </Button>
                </span>
              )}
            </li>
          ),
        )}
      </ol>
    </section>
  );
}

function EditTouch({
  data,
  courseId,
  touch,
  onResult,
  done,
}: {
  data: TrackerData;
  courseId: UUID;
  touch: Touch;
  onResult: (result: PersonResult) => void;
  done: () => void;
}) {
  const { config, today } = data;
  const role = stageRole(config, touch.stageKey);
  const isMoney = touch.kind === 'payment' || role === 'won';
  const hasFor = touch.kind === 'step' && (role === 'booked' || role === 'decision');
  const [happenedOn, setHappenedOn] = useState(touch.happenedOn);
  const [forOn, setForOn] = useState(touch.forOn ?? '');
  const [amount, setAmount] = useState(moneyInputValue(touch.amountCents));
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);

  async function save(e: Event) {
    e.preventDefault();
    if (happenedOn > today) {
      setError(FUTURE_DATE_MESSAGE);
      return;
    }
    const amountCents = isMoney ? parseMoneyCents(amount) : undefined;
    if (isMoney && !amountCents) {
      setError(role === 'won' ? AMOUNT_BOOKED_MESSAGE : AMOUNT_COLLECTED_MESSAGE);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const result = await rpx.tracker.updateTouch({
        courseId,
        touchId: touch.id as UUID,
        happenedOn,
        forOn: hasFor && forOn ? forOn : undefined,
        amountCents,
      });
      onResult(result);
      done();
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="flex flex-col gap-2" onSubmit={save}>
      <span class="text-sm text-gray-900 dark:text-gray-100">
        {touchText(config, touch, today)}
      </span>
      <div class="flex flex-wrap gap-3">
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>Date</span>
          <input
            type="date"
            class={INPUT_CLASS}
            value={happenedOn}
            max={today}
            onChange={(e) => setHappenedOn(e.currentTarget.value)}
          />
        </label>
        {hasFor && (
          <label class="flex flex-col gap-1">
            <span class={LABEL_CLASS}>{role === 'booked' ? 'Conversation on' : 'Decision on'}</span>
            <input
              type="date"
              class={INPUT_CLASS}
              value={forOn}
              min={role === 'booked' ? happenedOn : undefined}
              onChange={(e) => setForOn(e.currentTarget.value)}
            />
          </label>
        )}
        {isMoney && (
          <label class="flex flex-col gap-1">
            <span class={LABEL_CLASS}>{role === 'won' ? 'Amount booked' : 'Amount collected'}</span>
            <input
              class={INPUT_CLASS}
              inputMode="decimal"
              value={amount}
              onInput={(e) => setAmount(e.currentTarget.value)}
            />
          </label>
        )}
      </div>
      <FieldError id={`touch-${touch.id}-error`} message={error} />
      <div class="flex gap-2">
        <BtnPrimary type="submit" isLoading={saving}>
          Save
        </BtnPrimary>
        <BtnSecondary type="button" onClick={done}>
          Cancel
        </BtnSecondary>
      </div>
    </form>
  );
}

const DETAIL_FIELDS: Array<{
  key: keyof TrackerPersonFields;
  label: string;
  field?: TrackerFieldKey;
}> = [
  { key: 'firstName', label: 'First name' },
  { key: 'fullName', label: 'Full name' },
  { key: 'offer', label: 'Offer', field: 'offer' },
  { key: 'email', label: 'Email', field: 'email' },
  { key: 'phone', label: 'Phone', field: 'phone' },
  { key: 'segment', label: 'Segment', field: 'segment' },
  { key: 'tier', label: 'Tier', field: 'tier' },
  { key: 'note', label: 'Note', field: 'note' },
];

function EditDetails({
  data,
  courseId,
  person,
  touches,
  onResult,
  done,
}: Props & { done: () => void }) {
  const { config } = data;
  const [values, setValues] = useState<TrackerPersonFields>({
    firstName: person.firstName,
    fullName: person.fullName,
    offer: person.offer,
    email: person.email,
    phone: person.phone,
    segment: person.segment,
    tier: person.tier,
    note: person.note,
  });
  const [channelKey, setChannelKey] = useState(person.channelKey);
  const [betId, setBetId] = useState(person.betId);
  const movesTo = betId !== person.betId ? betOf(data.bets, betId) : undefined;
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const hasSteps = touches.some((t) => t.kind === 'step');

  async function save(e: Event) {
    e.preventDefault();
    if (!values.firstName.trim()) {
      setError('Add a first name.');
      return;
    }
    if (config.bets && person.betId && !betId) {
      setError(PICK_A_BET_MESSAGE);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const result = await rpx.tracker.updatePerson({
        courseId,
        personId: person.id as UUID,
        person: values,
        channelKey: channelKey !== person.channelKey ? channelKey : undefined,
        betId: config.bets && betId && betId !== person.betId ? (betId as UUID) : undefined,
      });
      onResult(result);
      done();
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class="flex flex-col gap-4" onSubmit={save}>
      {config.bets && (
        <div class="flex flex-col gap-1">
          <BetSelect bets={data.bets} value={betId} optional={!person.betId} onChange={setBetId} />
          {movesTo && touches.length > 0 && (
            <span class={HELP_CLASS}>
              Moves {touches.length} {touches.length === 1 ? 'touch' : 'touches'} to {movesTo.name}
            </span>
          )}
        </div>
      )}
      {DETAIL_FIELDS.filter(
        (f) =>
          (!f.field || config.fields[f.field]) &&
          !(config.bets && person.betId && f.key === 'offer'),
      ).map((f) => (
        <label key={f.key} class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>{f.label}</span>
          {f.key === 'note' ? (
            <textarea
              class={INPUT_CLASS}
              rows={3}
              maxLength={FIELD_LIMITS.note}
              value={values.note}
              onInput={(e) => setValues({ ...values, note: e.currentTarget.value })}
            />
          ) : (
            <input
              class={INPUT_CLASS}
              maxLength={FIELD_LIMITS[f.key]}
              value={values[f.key]}
              onInput={(e) => setValues({ ...values, [f.key]: e.currentTarget.value })}
            />
          )}
        </label>
      ))}
      {config.channels.length > 1 && (
        <label class="flex flex-col gap-1">
          <span class={LABEL_CLASS}>Channel</span>
          <select
            class={INPUT_CLASS}
            value={channelKey}
            disabled={hasSteps}
            onChange={(e) => setChannelKey(e.currentTarget.value)}
          >
            {config.channels.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          {hasSteps && <span class={HELP_CLASS}>Has logged steps</span>}
        </label>
      )}
      <FieldError id="details-error" message={error} />
      <div class="flex gap-2">
        <BtnPrimary type="submit" isLoading={saving}>
          Save
        </BtnPrimary>
        <BtnSecondary type="button" onClick={done}>
          Cancel
        </BtnSecondary>
      </div>
    </form>
  );
}

function DeletePerson({ courseId, person, touches, onDeleted }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const name = personName(person);

  async function remove() {
    setDeleting(true);
    try {
      await rpx.tracker.deletePerson({ courseId, personId: person.id as UUID });
      onDeleted(person.id);
    } catch (err) {
      setError(readError(err).message);
      setDeleting(false);
    }
  }

  return (
    <section class="mt-auto pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-col gap-3">
      {confirming ? (
        <>
          <p class="text-sm text-gray-900 dark:text-gray-100">
            Delete {name} and all {touches.length} {touches.length === 1 ? 'touch' : 'touches'}?
            Your totals for past weeks drop too.
          </p>
          <FieldError id="delete-error" message={error} />
          <div class="flex gap-2">
            <BtnWarning onClick={remove} isLoading={deleting}>
              Delete person
            </BtnWarning>
            <BtnSecondary onClick={() => setConfirming(false)}>Keep</BtnSecondary>
          </div>
        </>
      ) : (
        <Button
          class="text-sm text-red-700 dark:text-red-400 underline self-start"
          onClick={() => setConfirming(true)}
        >
          Delete person
        </Button>
      )}
    </section>
  );
}
